<template>
  <div>
    <v-dialog v-model="value"
      content-class="right-dialog rounded-r-0" 
      transition="slide-x-reverse-transition" 
      max-width="1200" 
      scrollable
      persistent>
      <v-card outlined v-if="orderData" :loading="isSaving || isLoading">
        <v-overlay absolute v-if="isLoading || isSaving" opacity="0.5" color="white">
          <v-progress-circular indeterminate color="green" size="70"></v-progress-circular>
        </v-overlay>
        <v-card-title class="d-flex justify-space-between align-center primary white--text">
         <div class="d-flex align-center">
            <v-btn color="white" icon @click="$emit('input', false)">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <div class="font-weight-regular">{{ $t('customer.order_number') }}  {{ orderData.documentId}}</div>
         </div>
         <template v-if="orderData.isPaid">
            <v-chip class="chip-status success">
              <v-icon left x-small> mdi-circle </v-icon>
              {{ $t('finish') }}
            </v-chip>
         </template>
         <template v-else-if="!orderData.isPaid && !orderData.receipts && !orderData.isPaymentApprove">
            <v-chip class="chip-status error">
              <v-icon left x-small> mdi-circle </v-icon>
              {{ $t('payment.pending') }}
            </v-chip>
         </template>
         <template v-else-if="!orderData.isPaid && orderData.receipts">
            <v-chip class="chip-status error">
              <v-icon left x-small> mdi-circle </v-icon>
              {{ $t('cancel') }}
            </v-chip>
         </template>
         <template v-else-if="!orderData.isPaid && !orderData.receipts && orderData.isPaymentApprove">
            <v-chip class="chip-status warning">
              <v-icon left x-small> mdi-circle </v-icon>
              {{ $t('customer.waiting_confirm') }}
            </v-chip>
         </template>
        </v-card-title>

        <v-card-title class="d-block">
          <div class="flex justify-between align-center">
            <div class="flex align-center gap-2 darkprimary--text">
              <h3 class="font-weight-bold">{{ $t('meter.title') }}{{ orderData.meter && orderData.meter.isMain ? $t('main') : $t('additional') }}</h3>
              <div v-if="orderData.meter && orderData.meter.digitalMeterData && orderData.meter.digitalMeterData.type == 'prepaid'">
                <v-chip class="chip-status info">
                  {{ $t('prepaid') }}
                </v-chip>
              </div>
              <h3>({{ $t('area.space') }} {{orderData.meter && orderData.meter.area ? orderData.meter.area.name : '-'}})</h3>
            </div>
          </div>  

          <v-alert
            v-if="orderData.isSuccess && orderData.successAt"
            border="left"
            color="success"
            dense
            text
            type="success"
            class="alert-on-off mt-3 mb-4">
            {{ $t('meter.add_digital_electricity_meter_success_date', { date: $helper.dateTimeTH(orderData.successAt.iso) }) }}
          </v-alert>

          <v-alert
            v-else-if="!orderData.isSuccess && orderData.lastUpdateSuccessAt" 
            border="left"
            color="error"
            dense
            text
            type="info"
            class="alert-on-off mt-3 mb-4">
            {{ $t('validate.add_unit_error_because_meter_offline_please_check_meter') }} 
            <template v-if="orderData.updateSuccessCount < 10">
              {{ $t('validate.system_will_add_unit_automatically_again_within_the_day') }} 
              {{ $moment(orderData.lastUpdateSuccessAt.iso).add(15, 'minutes').format('DD MMM YYYY, HH:mm') }} {{ $t('timeunit.minute.short') }}
            </template>
          </v-alert>
        </v-card-title>
        <v-card-text>
          <div>
            <v-row>
              <v-col cols="6">
                <div class="box-bordered">
                  <div class="header pb-3 mb-3">
                    <h4 class="darkprimary_2--text font-weight-bold">
                      {{ $t('customer.customer_information') }}
                    </h4>
                  </div>
                  <div class="body">
                    <div class="d-flex justify-between mb-2">
                      <div class="black--text">{{ $t('customer.company_name_and_customer_name') }}:</div>
                      <div class="black--text text-right">
                        {{ orderData.customerData.name || '-'}}
                      </div>
                    </div>


                    <div v-if="orderData.booking && orderData.contract || orderData.booking && !orderData.contract" class="d-flex justify-between mb-2">
                      <div class="black--text">{{ $t('area.shop_name') }}:</div>
                      <div class="black--text text-right">{{orderData.booking.businessName}}</div>
                    </div>
                    <div v-else-if="orderData.contract" class="d-flex justify-between mb-2">
                      <div class="black--text">{{ $t('area.shop_name') }}:</div>
                      <div class="black--text text-right">{{orderData.contract.businessName}}</div>
                    </div>

                    <div class="d-flex justify-between mb-2">
                      <div class="black--text">{{ $t('address.company_branch') }}:</div>
                      <div class="black--text text-right">
                        {{orderData.customerData.branchName ? orderData.customerData.branchName : '-'}}
                      </div>
                    </div>

                    <div class="d-flex justify-between mb-2">
                      <div class="black--text">{{ $t('account.address') }}:</div>
                      <div class="black--text text-right" style="max-width:200px">
                        <div class="mb-2"> {{ orderData.customerData.address || '-'}}</div>
                      </div>
                    </div>

                    <div class="d-flex justify-between mb-2">
                      <div class="black--text">{{ $t('tax.tax_id_number_another_form') }}:</div>
                      <div class="black--text text-right">
                        {{ orderData.customerData.taxId || '-'}}
                      </div>
                    </div>

                    <div class="d-flex justify-between mb-2">
                      <div class="black--text">{{ $t('account.phone_number') }}:</div>
                      <div class="black--text text-right">
                        {{ orderData.customerData.tel || '-'}}
                      </div>
                    </div>

                    <div class="d-flex justify-between mb-2">
                      <div class="black--text">Email:</div>
                      <div class="black--text text-right">
                        {{ orderData.customerData.email || '-'}}
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="6">
                <v-row>
                  <v-col cols="5">
                    <v-subheader>
                      <b>{{ $t('payment.currency') }}:</b>
                    </v-subheader>
                  </v-col>
                  <v-col cols="7">
                    <v-select v-model="orderData.currencyCode" :items="$helper.currencyOptions" item-text="value" item-value="value"
                      outlined dense hide-details disabled :no-data-text="$t('noinfo')"></v-select>
                  </v-col>
                </v-row>
                <div class="box-bordered">
                  <div class="header pb-3 mb-3">
                    <h4 class="darkprimary_2--text font-weight-bold">
                      {{ $t('customer.last_status') }}
                    </h4>
                  </div>

                  <div class="body">
                    <div v-if="logs && logs.length" class="d-flex justify-between align-center">
                      <v-chip v-if="logs[0].activity == 'paid'"
                        color="green"
                        text-color="white"
                      >
                        <span class="mx-1">{{ $t('payment.pay') }}</span>
                      </v-chip>
                      <v-chip v-if="logs[0].activity == 'sent'"
                        color="blue"
                        text-color="white"
                      >
                        <span class="mx-1">{{ $t('customer.customer_buy_unit') }}</span>
                      </v-chip>
                      <v-chip v-if="logs[0].activity == 'unpaid'"
                        color="red"
                        text-color="white"
                      >
                        <span class="mx-1">{{ $t('payment.cancel_pay_money') }}</span>
                      </v-chip>
                      <div class="text-right">
                        <div><b>{{ $t('date') }}</b>: {{ logs[0].createdAt ? $helper.dateTimeTH(logs[0].createdAt) : '-'}} </div>
                        <div>
                            <b>{{ $t('customer.operation_by') }}</b>: 
                            <template v-if="logs[0].user">
                            <template v-if="logs[0].user.firstName">
                              {{ logs[0].user.firstName }} {{ logs[0].user.lastName }}
                            </template>
                            <template v-else>
                              {{ logs[0].user.username }}
                            </template>
                          </template>
                          <template v-else>
                            -
                          </template>
                        </div>
                      </div>
                    </div>
                    <template v-else>
                      <div class="gray--text text-center mb-5">
                        {{ $t('noinfo') }}
                      </div>
                    </template>
                  </div>
                  <div class="actions d-flex justify-center mt-3">
                    <div class="d-inline-flex cursor-pointer" @click="dialogLogActivity = true">
                      <v-icon class="mx-1" small>
                        mdi-clock-time-eight-outline
                      </v-icon>
                      <h5 class="text-decoration-underline">{{ $t('view_latest_history') }}</h5>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
          <div class="box-list-product">
            <div  class="d-flex justify-end mb-5" >
              <div style="max-width:250px;width:100%;">
                <v-row>
                  <v-col cols="5" class="py-0">
                    <p class="summary-topic font-weight-bold">{{ $t('product.product_price') }}:</p>
                  </v-col>
                  <v-col cols="7" class="py-0 ps-0 text-right">
                    <p class="summary-number pe-5 me-2">{{project && project.isVatInclusive ? $t('tax.total_tax') : $t('payment.price_vat_exclusive')}}</p>
                  </v-col>
                </v-row>
              </div>
            </div>

            <div style="border-radius: 10px; overflow: hidden">
              <v-simple-table v-if="orderData.items && orderData.items.length">
                <template v-slot:default>
                  <thead class="table-item header rounded-2">
                    <tr>
                      <th width="3%" class="text-center">{{ $t('rank') }}</th>
                      <th>{{ $t('product.product_name_and_detail') }}</th>
                      <th>{{ $t('area.space') }}</th>
                      <th width="8%" class="text-center">{{ $t('product.quantity') }}</th>
                      <th width="8%">{{ $t('unit') }}</th>
                      <th width="10%" class="text-center">{{ $t('product.unit_price') }}</th>
                      <th width="10%" class="text-center">{{ $t('product.discount_unit') }}</th>
                      <th width="10%">{{ $t('tax.tax_vat') }}</th>
                      <th width="10%">{{ $t('tax.tax_withholding') }}</th>
                      <th width="10%" class="text-center">{{ $t('product.total_price') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in orderData.items" :key="index">
                      <td class="py-2 text-center vertical-top">{{ index + 1 }}</td>
                      <td class="py-2 vertical-top">
                        {{item.name}}
                        {{item.detail ? ' / ' + item.detail : ''}}
                      </td>
                      <td class="py-2 vertical-top">
                        {{item.area ? item.area.name : '-'}}
                        {{item.area ? `(${item.area.size}) ${$t('square_meter')}` : '-'}}
                      </td>
                      <td class="py-2 text-center vertical-top">
                        {{ $helper.toCurrency(item.quantity) }}
                      </td>
                      <td class="py-2 text-center vertical-top">
                        {{item.unit}}
                      </td>
                      <td class="py-2 text-center vertical-top">
                        {{ $helper.toCurrency(item.originalPricePerUnit) }}
                      </td>
                      <td class="py-2 text-center vertical-top">
                        {{ $helper.toCurrency(item.discountPerUnit) }}
                      </td>
                      <td class="py-2 text-center vertical-top">
                        {{ item.sellVatType == 1 
                          ? orderData.vatPercent + '%'
                          : item.sellVatType == 2
                          ? '0%'
                          : item.sellVatType == 3 
                          ? $t('except') 
                          : '-'
                          }} 
                      </td>
                      <td class="py-2 text-center vertical-top">
                        {{ item.totalWhtValue | currency }}
                      </td>
                      <td class="py-2 text-center vertical-top">
                        <b>{{ item.totalValue | currency }}</b>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </div>

          <!-- Summary -->
          <div class="box-payment-container pt-5 mt-5">
            <div class="box-payment ">
              <div>
                <v-row class="mb-3">
                  <v-col cols="7" class="py-0">
                    <p class="summary-topic font-weight-bold darkprimary_2--text">{{ $t('payment.title') }}</p>
                  </v-col>
                  <v-col cols="5" class="py-0 text-right">
                    <template v-if="orderData.isPaid">
                      <v-chip class="chip-status success">
                        <v-icon left x-small> mdi-circle </v-icon>
                        {{ $t('payment.paid_money') }}
                      </v-chip>
                  </template>
                  <template v-else-if="!orderData.isPaid && !orderData.receipts && !orderData.isPaymentApprove">
                      <v-chip class="chip-status error">
                        <v-icon left x-small> mdi-circle </v-icon>
                        {{ $t('payment.pending') }}
                      </v-chip>
                  </template>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="7" class="py-0">
                    <p class="summary-topic font-weight-bold">{{ $t('payment.total_amount') }}:</p>
                  </v-col>
                  <v-col cols="5" class="py-0 text-right">
                    <p class="summary-number font-weight-bold">{{ orderData.totalValue | currencyPrefixSign }}</p>
                  </v-col>
                </v-row>
                <div class="divider-dashed my-4"></div>
                <v-row>
                  <v-col cols="7" class="py-0">
                    <p class="summary-topic">{{ $t('payment.total_discount_amount_value') }}:</p>
                  </v-col>
                  <v-col cols="5" class="py-0 text-right">
                    <p class="summary-number">{{ orderData.totalDiscountValue | currencyPrefixSign }}</p>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="7" class="py-0">
                    <p class="summary-topic">{{ $t('tax.tax_exempt_value') }}:</p>
                  </v-col>
                  <v-col cols="5" class="py-0 text-right">
                    <p class="summary-number">{{ orderData.totalNonVatItemsValue | currencyPrefixSign }}</p>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="7" class="py-0">
                    <p class="summary-topic">{{ $t('tax.tax_calculated_value') }}:</p>
                  </v-col>
                  <v-col cols="5" class="py-0 text-right">
                    <p class="summary-number">{{ orderData.totalVatItemsValue | currencyPrefixSign }}</p>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="7" class="py-0">
                    <p class="summary-topic">{{ $t('tax.tax_vat') }}:</p>
                  </v-col>
                  <v-col cols="5" class="py-0 text-right">
                    <p class="summary-number">{{ orderData.totalVatValue | currencyPrefixSign }}</p>
                  </v-col>
                </v-row>
                <div class="text-right"></div>
                <div class="divider-dashed my-4"></div>
                <v-row>
                  <v-col cols="7" class="py-0">
                    <p class="summary-topic bold">{{ $t('invoice.installment_total_amount') }}</p>
                  </v-col>
                  <v-col cols="5" class="py-0 text-right">
                    <p class="summary-number bold">{{ orderData.totalValue | currencyPrefixSign }}</p>
                  </v-col>
                </v-row>
                <div>
                  <div class="divider-dashed mt-4"></div>
                  <v-row class="mt-2" v-if="isWht">
                    <v-col cols="7" class="py-0">
                      <p class="summary-topic">{{ $t('tax.tax_withholding') }}:</p>
                    </v-col>
                    <v-col cols="5" class="py-0 text-right">
                      <p class="summary-number">{{ orderData.totalWhtValue | currencyPrefixSign }}</p>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </div>
          </div> 
          
          <div class="mt-5">
            <v-alert color="#E7FCE3">
              <v-row class="py-2">
                <v-col cols="7" class="py-0">
                  <p class="summary-topic bold darkprimary--text">{{ $t('payment.total_amount_to_pay') }}</p>
                </v-col>
                <v-col cols="5" class="py-0 text-right">
                  <p class="summary-number bold darkprimary--text">
                    {{
                    (orderData.totalValue - orderData.totalWhtValue)
                      | currencySuffixTextThEn
                    }}
                  </p>
                </v-col>
              </v-row>
            </v-alert>
          </div>

          <v-card class="pb-3 my-10 ms-1" v-if="orderData && orderData.invoicePayment">
            <v-card-text>
              <h2 class="py-0 green--text">{{ $t('payment.notify_payment') }}</h2>
            </v-card-text>
            <v-card-text>
              <v-row>
                <v-col cols="6" class>
                  <a v-for="(img, index) in orderData.invoicePayment.paymentProofFiles"
                    class="w-100 flex items-center justify-center mb-3" :key="index" :href="img.url" target="_blank">
                    <v-img max-width="500" :src="img.url" />
                  </a>
                </v-col>
                <v-col cols="6" v-if="orderData.invoicePayment.paymentBankAccount" style="font-size: 18px">
                  <h3 class="mb-5">
                    <b>{{ $t('payment.transfer_to_account') }}</b>
                  </h3>
                  <p>
                    <b class="me-2">{{ $t('payment.bank') }} :</b>
                    <img style="display: inline;border-radius: 50%;" width="40"
                      :src="require(`../assets/images/bank/${orderData.invoicePayment.paymentBankAccount.bankCode}.jpg`)" />
                    {{ BankData[orderData.invoicePayment.paymentBankAccount.bankCode].name }}
                  </p>
                  <p>
                    <b>{{ $t('payment.account_number') }} :</b>
                    {{ orderData.invoicePayment.paymentBankAccount.accountName }}
                  </p>
                  <p>
                    <b>{{ $t('payment.account_name') }} :</b>
                    {{ orderData.invoicePayment.paymentBankAccount.accountNumber }}
                  </p>
                  <br />
                  <p>
                    <b>{{ $t('payment.attached_when') }} :</b>
                    {{ $moment(orderData.invoicePayment.createdAt).format("lll") }}
                  </p>
                  <p>
                    <b>{{ $t('payment.attached_by') }} :</b>
                    {{ orderData.invoicePayment.customerUser.user.firstName }} {{
                      orderData.invoicePayment.customerUser.user.lastName }}
                  </p>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card class="pb-3 my-3 ms-1">
            <v-card-text>
              <h3 class="py-0 green--text">{{ $t('payment.save_payment') }}</h3>
            </v-card-text>
            <div class="px-5">
              <v-simple-table>
                <template v-slot:default>
                  <thead class="table-item header rounded-2">
                    <tr>
                      <th class="text-no-wrap">{{ $t('bill.receipt_id') }}</th>
                      <th class="text-no-wrap">{{ $t('payment.pay_date') }}</th>
                      <th class="text-right text-no-wrap">{{ $t('payment.net_amount') }}</th>
                      <th class="text-right text-no-wrap">{{ $t('tax.tax_deducted') }} <br>{{ $t('tax.withholding') }}</th>
                      <th class="text-right text-no-wrap">{{ $t('payment.received_amount') }}</th>
                      <th class="text-center text-no-wrap">{{ $t('payment.payment_method') }}</th>
                      <th class="text-no-wrap">{{ $t('status') }}</th>
                      <th class="text-no-wrap">{{ $t('note') }}</th>
                      <th class="text-no-wrap"></th>
                    </tr>
                  </thead>
                  <tbody v-if="orderData.receipts && !isLoading">
                    <tr v-for="receipt in orderData.receipts" :key="receipt.objectId" @click="tempReceipt = receipt,isReceiptDialog = true">
                      <td >
                        {{ receipt.documentId }}
                      </td>
                      <td class="align-vertical">{{ `${$moment(receipt.date).format("DD/MM/YYYY")}` }}</td>
                      <td class="text-right">{{ receipt.totalValue | currencyPrefixSign }}</td>
                      <td class="text-right">{{ receipt.totalWhtValue | currency }}</td>
                      <td class="text-right">
                        {{
                          (receipt.totalValue - receipt.totalWhtValue)
                          | currencyPrefixSign
                        }}
                      </td>
                      <td class="text-center text-no-wrap">
                          <span v-if="receipt.paymentMethod == 1">{{ $t('payment.cash') }}</span>
                          <span v-if="receipt.paymentMethod == 2">
                            {{ $t('payment.bank_transfer_money')}}
                            <br />
                            [{{
                              receipt.paymentBankAccount ? BankData[receipt.paymentBankAccount.bankCode].name : '-'
                            }}]
                            <br />
                            [{{ $t('number') }}:{{
                              receipt.paymentBankAccount ? receipt.paymentBankAccount.accountNumber : '-'
                            }}]
                            <br />
                            <template v-for="(
                                img, index
                              ) of receipt.paymentProofFiles">
                              <a :href="img.url" target="_blank" :key="`img_${index}`" @click.stop>{{ $t('customer.proof_number', { index: index + 1 }) }}</a>
                            </template>
                          </span>
                          <span v-if="receipt.paymentMethod == 3">{{ $t('payment.credit_card') }}</span>
                          <span v-if="receipt.paymentMethod == 4">{{ $t('payment.bill_payment') }}</span>
                          <span v-if="receipt.paymentMethod == 5">{{ $t('payment.deposit_deduction') }}</span>
                          <span v-if="receipt.paymentMethod == 6">
                            {{ $t('payment.cash_check') }}
                            <br />
                            [{{
                              receipt.paymentBankAccount ? BankData[receipt.paymentBankAccount.bankCode].name : '-'
                                .name
                            }}]
                            <br />
                            [{{ $t('number') }}:{{
                              receipt.paymentBankAccount ? receipt.paymentBankAccount.accountNumber : '-'
                            }}]
                            <br />
                          </span>
                          <span v-if="receipt.paymentMethod == 7">QR Payment</span>
                          <span v-if="receipt.paymentMethod == 8">
                            {{ $t('payment.gift_voucher') }}
                            <span v-if="receipt.voucherNumber">
                              <br />
                              [{{ $t('number') }}:{{ receipt.voucherNumber }}]
                            </span>
                            <br />
                            <template v-for="(img, index) of receipt.paymentProofFiles">
                              <a :href="img.url" target="_blank" :key="`img_${index}`" @click.stop>{{ $t('customer.proof_number', { index: index + 1 }) }}</a>
                            </template>
                          </span>
                      </td>
                      <td>
                        <span v-if="receipt.isPaid" class="green--text text-no-wrap">{{ $t('payment.paid') }}</span>
                        <span v-if="!receipt.isPaid && !receipt.documentId" class="red--text text-no-wrap">{{ $t('payment.pending') }}</span>
                        <span v-if="!receipt.isPaid && receipt.documentId" class="red--text text-no-wrap">{{ $t('payment.cancel_payment') }}</span>
                      </td>
                      <td>{{ receipt.paymentNote }}</td>
                      <td class="text-center">
                        <template>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <span v-bind="attrs" v-on="!enableEditInvoiceAndReceipt ? on : null">
                                <v-btn v-if="!receipt.isPaid" color="success" elevation="2"
                                  class="mx-2 my-1" @click.stop="openPaymentByReceipt(receipt)"
                                  :disabled="isSaving || isLoading || !enableEditInvoiceAndReceipt">{{ $t('payment.pay_money') }}</v-btn>
                              </span>
                            </template>
                            <span v-if="!enableEditInvoiceAndReceipt">{{ $t('validate.you_do_not_have_permission_to_edit_bill') }}</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <span v-bind="attrs"
                                v-on="!enableEditInvoiceAndReceipt ? on : null">
                                <v-btn v-if="receipt.isPaid" color="error" elevation="2" class="mx-2 my-1"
                                  @click.stop="openDialogConfirmUnpaid(receipt.objectId)"
                                  :disabled="isSaving || isLoading || !enableEditInvoiceAndReceipt">
                                  {{ $t('payment.cancel_payment') }}</v-btn>
                              </span>
                            </template>
                            <span v-if="!enableEditInvoiceAndReceipt">{{ $t('validate.you_do_not_have_permission_to_edit_bill') }}</span>
                          </v-tooltip>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <div class="py-5 text-center" v-if="!orderData.receipts || (orderData.receipts && !orderData.receipts.length)">
                --- {{ $t('payment.no_payment') }} ---
              </div>
            </div>
          </v-card>  
        </v-card-text>

        <v-divider></v-divider>
       
        <v-card-actions v-if="enableEditInvoiceAndReceipt">
          <v-spacer></v-spacer>
          <template v-if="orderData.isPaymentApprove">
            <v-btn
            color="error"
            type="submit"
            elevation="0"
            :disabled="isLoading || isSaving"
            :loading="isSaving"
            @click="dialogCancelOrder = true">
            {{ $t('payment.cancel_order') }}</v-btn>
          <v-btn
            color="success"
            type="submit"
            elevation="0"
            :disabled="isLoading || isSaving"
            :loading="isSaving"
            @click="dialogConfirmOrder = true">
            {{ $t('payment.confirm_order') }}</v-btn>
          </template>
          <template v-else-if="!orderData.isPaid && !orderData.receipts">
            <v-btn
              color="success"
              type="submit"
              elevation="0"
              :disabled="isSaving || orderData.totalValue - orderData.totalWhtValue <= 0"
              :loading="isSaving"
              @click="openPaymentByOrder">
            {{ $t('payment.pay') }}
          </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogCancelOrder" persistent max-width="400px">
      <v-card>
        <v-card-title class="justify-center">{{ $t('payment.cancel_order') }}</v-card-title>
        <v-card-text>
          <div class="black--text text-center my-5">
            <div class="mb-2">{{ $t('validate.you_want_to_cancel_order') }}</div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <div class="flex justify-between">
            <v-btn color="grey" width="49%" outlined @click="dialogCancelOrder = false" :disabled="isSaving">{{ $t('cancel') }}</v-btn>
            <v-btn color="error" width="49%" @click="confirmCancelOrder" elevation="0" :disabled="isSaving" :loading="isSaving">{{ $t('confirm') }}</v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogConfirmOrder" persistent max-width="400px">
      <v-card>
        <v-card-title class="justify-center">{{ $t('payment.confirm_order') }}</v-card-title>
        <v-card-text>
          <div class="black--text text-center my-5">
            <div class="mb-2">{{ $t('validate.you_want_to_confirm_order') }}</div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <div class="flex justify-between">
            <v-btn color="grey" width="49%" outlined @click="dialogConfirmOrder = false" :disabled="isSaving">{{ $t('cancel') }}</v-btn>
            <v-btn color="success" width="49%" @click="confirmOrder" :disabled="isSaving" elevation="0" :loading="isSaving">{{ $t('confirm') }}</v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogLogActivity"
      max-width="600"
      scrollable
    >
      <v-card v-if="!isLoading" class="pb-2" style="height: 100%;">
        <v-card-title
          class="d-flex justify-space-between align-center primary white--text py-2"
        >
          <span>{{ $t('latest_history') }}</span>
          <span>
            <v-icon
              @click="dialogLogActivity = false"
              color="white"
            >
              mdi-close
            </v-icon>
          </span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="height: 500px;" class="px-0 py-0" v-if="logs && logs.length">
          <v-card v-for="log in logs" :key="log.objectId" class="my-3 mx-4 shadow-card-bill">
            <v-list-item class="py-2" >
              <v-list-item-avatar 
                class="py-0 text-center" 
                tile
                min-width="250"
              >
                <v-row
                  justify="center"
                  align="center"
                  class="py-0 my-0"
                >
                  <v-chip
                    v-if="log.activity == 'paid'"
                    color="green"
                    text-color="white"
                  >
                    <span class="mx-1">{{ $t('payment.pay') }}</span>
                  </v-chip>
                  <!-- <v-chip
                    v-else-if="log.activity == 'confirm_paid'"
                    color="green"
                    text-color="white"
                  >
                    <span class="mx-1">ยืนยันชำระเงิน</span>
                  </v-chip> -->
                  <v-chip
                    v-else-if="log.activity == 'sent'"
                    color="blue"
                    text-color="white"
                  >
                    <span class="mx-1">{{ $t('customer.customer_buy_unit') }}</span>
                  </v-chip>
                  <v-chip
                    v-else-if="log.activity == 'unpaid'"
                    color="red"
                    text-color="white"
                  >
                    <span class="mx-1">{{ $t('payment.cancel_pay_money') }}</span>
                  </v-chip>

                  <span v-else>{{ $t('no') }}</span>
                </v-row>
              </v-list-item-avatar>
              <v-list-item-content>
                <span class="text-color-black">
                  <p class="py-0 my-2"><b>{{ $t('date') }} : </b> {{ `${$helper.dateTimeTH(log.createdAt)}` }}</p>
                  <p class="py-0 my-2"><b>{{ $t('customer.operation_by') }} : </b> 
                    <span v-if="log.user">
                      <template v-if="log.user.firstName">
                        {{ log.user.firstName }} {{ log.user.lastName }}
                      </template>
                      <template v-else>
                        {{ log.user.username }}
                      </template>
                    </span>
                    <span v-else> - </span>
                  </p>
                </span>
              </v-list-item-content>
            </v-list-item>
          </v-card>
        </v-card-text>
        <v-card-text v-else justify="center" align="center" class="py-5">
          <h3>{{ $t('no') }}</h3>
        </v-card-text>
        <v-divider></v-divider>
      </v-card>
    </v-dialog>

    <PaymentFormDialog 
      v-model="isPaymentFormDialog" 
      :projectId="projectId" 
      :order="tempPayment"
      :receipt="tempPaymentReceipt"
      :isHideSplitPay="true"
      :paymentMethod="tempInvoicePayment && tempInvoicePayment.paymentMethod ? tempInvoicePayment.paymentMethod : null"
      :paymentProofFiles="tempInvoicePayment && tempInvoicePayment.paymentProofFiles ? tempInvoicePayment.paymentProofFiles : undefined"
      :paymentBankAccount="tempInvoicePayment && tempInvoicePayment.paymentBankAccount ? tempInvoicePayment.paymentBankAccount : undefined"
      @onSaveSuccess="onSaveSuccessPaymentFn" 
    />

    <ReceiptDialog v-if="tempReceipt" v-model="isReceiptDialog" :projectId="projectId"
      :receiptId="tempReceipt.objectId" />

    <v-dialog v-model="isUnpaidConfirmDialog" persistent max-width="400px">
      <v-card>
        <v-card-title class="justify-center">{{ $t('bill.cancel_receipt') }}</v-card-title>
        <v-card-text>
          <div class="black--text text-center my-5">
            <div class="mb-2">{{ $t('validate.you_want_to_cancel_receipt') }}</div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <div class="flex justify-between">
            <v-btn color="grey" width="49%" outlined @click="isUnpaidConfirmDialog = false" :disabled="isSaving">{{ $t('cancel') }}</v-btn>
            <v-btn color="error" width="49%" @click="cancelUnpaidReceipt" elevation="0" :disabled="isSaving" :loading="isSaving">{{ $t('confirm') }}</v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapMutations ,mapActions} from "vuex";
import PaymentFormDialog from "./paymentFormDialog.component.vue";
import ReceiptDialog from "../components/ReceiptDialog.component.vue";
import { DocumentItem , Order} from "../classes";
import ConfirmDialog from "@/components/ConfirmDialog.component";
import BankData from "../json/bank-data.json";

export default {
  components: {
    PaymentFormDialog,
    ReceiptDialog,
    ConfirmDialog
  }, 
  computed: {
    admins() {
      return this.$store.getters["admin/getByProjectId"](this.projectId);
    },
    employees() {
      return this.$store.getters["employee/getByProjectId"](this.projectId);
    },
    employee() {
      return this.$store.getters["employee/getByMarketId"](this.$route.params.marketId);
    },
    isWht() {
      if(this.orderData.items){
        return this.orderData.items.filter(d => d.whtPercent > 0).length > 0;
      }
    },
    enableEditInvoiceAndReceipt() {
      let res = false;
      if (
        this.admin ||
        (this.admins && this.admins.length) ||
        (this.employee && this.employee.enableEditInvoiceAndReceipt)
      ) {
        res = true;
      }
      return res;
    },
  },
  props : {
    value : Boolean,
    orderId : String,
    projectId : String,
  },
  data(){
    return {
      isLoading: false,
      isSaving: false,
      formData : {
        customerData: {},
        meterData: {
          unit : null,
          cost : null,
        }
      },
      BankData : BankData,
      customerType : '',
      historyData : [],
      selectHistory : null,
      swapMode : false,
      dialogCancelOrder : false,
      dialogConfirmOrder : false,
      dialogLogActivity : false,
      statusOrder : 4,
      orderData : null,
      receipt : null,
      tempInvoicePayment : {
        paymentMethod : null,
        paymentProofFiles : null,
        paymentBankAccount : null,
      },
      logs : null,
      isPaymentFormDialog : false,
      tempPayment : null,
      paymentOrderData : null,
      paymentMode : null,
      isReceiptDialog: false,
      tempReceipt: null,
      isUnpaidConfirmDialog : false,
      tempPaymentReceipt : null,
    }
  },
  async created() {
    this.isLoading = true;
    try {
      if (this.admins && this.admins.length) {
        this.project = this.admins[0].project;
      }
      if (this.employees && this.employees.length) {
        this.project = this.employees[0].project;
      }
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false
    }
  },
  mounted(){
  },
  methods: {
    ...mapActions({
      getOrder : 'order/getOrder', 
      getOrderLogs : 'orderLog/getOrderLogs', 
      sendOrder : 'order/sendOrder',
    }),
    async initData(){
      this.isLoading = true;
      this.tempPayment = null;
      this.tempInvoicePayment = null;
      this.tempPaymentReceipt = null;
      try {
        this.orderData = await this.getOrder(this.orderId);
        this.logs = await this.getOrderLogs(this.orderId);
        if(this.orderData.invoicePayment){
          this.tempInvoicePayment = {
            paymentMethod : this.orderData.invoicePayment.paymentMethod,
            paymentProofFiles : this.orderData.invoicePayment.paymentProofFiles,
            paymentBankAccount : this.orderData.invoicePayment.paymentBankAccount,
          };
        }
        this.isLoading = false;
      } catch (error) {
        this.$toast.show({
          content: error.message,
          color: "error"
        });
        this.isLoading = false;
      }
    },
    async confirmCancelOrder(){
      this.isSaving = true;
      try {
        let result = await this.$store.dispatch(
          "order/cancelOrderInvoicePayment",
          this.orderData.objectId
        );
        if(result){
          this.$toast.show({
          content: this.$t('cancel_success'),
          color: "success"
        });
        }
        await this.initData();
        this.$emit("onSaveSuccess");
        this.isSaving = false;
        this.dialogCancelOrder = false;
      } catch (error) {
        this.isSaving = false;
        this.dialogCancelOrder = false;
      }
    },
    openDialogConfirmUnpaid(receiptId){
      this.receiptId = receiptId
      this.isUnpaidConfirmDialog = true;
    },
    async cancelUnpaidReceipt({ note }){
      this.isSaving = true;
      try {
        let receipt = await this.$store.dispatch("receipt/unpaidReceipt", {
          id: this.receiptId,
          note
        });
        if (receipt.paymentMethod == 7) {
          this.$toast.show({
            content: this.$t('bill.cancel_success_please_check_refund_status_from_bill_status_history'),
            color: "success"
          });
        } else {
          this.$toast.show({
            content: this.$t('cancel_success'),
            color: "success"
          });
        }
        this.isUnpaidConfirmDialog = false;
        this.isSaving = false;
        await this.initData();
        this.$emit("onSaveSuccess");
      } catch (error) {
        this.isSaving = false;
        this.isUnpaidConfirmDialog = false;
      }
    },
    async confirmOrder(){
      this.isSaving = true;
      try {
        let receipt = null;
        let paymentData = {
          date: new Date(),
          paymentMethod: this.tempInvoicePayment.paymentMethod,
          paymentBankAccount: this.tempInvoicePayment.paymentBankAccount,
          paymentNote: this.tempInvoicePayment.paymentNote,
          paymentProofFiles: this.tempInvoicePayment.paymentProofFiles,
          voucherNumber: this.tempInvoicePayment.voucherNumber,
        };
        if(!this.orderData.isSent){
          await this.sendOrder({id : this.orderData.objectId, date: new Date()});
        }
        if(!this.orderData.receipts){
            receipt = await this.$store.dispatch("receipt/createReceipt", {
            orderId: this.orderData.objectId
          });
        }
        if (receipt) {
          paymentData.id = receipt.objectId;
          await this.$store.dispatch("receipt/payReceipt", paymentData);
        }
        this.$toast.show({
          content: this.$t('payment.payment_success'),
          color: "success"
        });
        await this.initData();
        this.$emit("onSaveSuccess");
        this.isSaving = false;
        this.dialogConfirmOrder = false;
        return true;
      } catch (error) {
        this.$toast.show({
          content: error.message,
          color: "error"
        });
        this.dialogConfirmOrder = false;
        this.isSaving = false;
      }
    },
    openPaymentByOrder(){
      this.tempPayment = this.orderData;
      this.paymentMode = 'Order';
      this.isPaymentFormDialog = true;
    },
    openPaymentByReceipt(receipt){
      this.tempPaymentReceipt = receipt;
      this.paymentMode = 'Receipt';
      this.isPaymentFormDialog = true;
    },
    async onSaveSuccessPaymentFn(data) {
      try {
        this.isSaving = true;
        await this.processPayment(data);
        
        this.$toast.show({
          content: this.$t('payment.payment_success'),
          color: "success"
        });
        await this.initData();
        this.$emit("onSaveSuccess");
        this.isSaving = false;
        return true;
      } catch (error) {
        this.$toast.show({
          content: error.message,
          color: "error"
        });
        this.isSaving = false;
      }
    },
    async processPayment(data) {
      if (!data || data.paymentMethod == 7) return;
      
      const paymentData = this.formatPaymentData(data);
      await this.ensureOrderSent();
      
      if (this.paymentMode == 'Order') {
        await this.processOrderPayment(paymentData);
      } else if (this.paymentMode == 'Receipt') {
        await this.processReceiptPayment(paymentData);
      }
    },
    async ensureOrderSent() {
      if (!this.orderData.isSent) {
        await this.sendOrder({
          id: this.orderData.objectId, 
          date: new Date()
        });
      }
    },
    async processOrderPayment(paymentData) {
      if (!this.tempPayment.receipts) {
        const receipt = await this.$store.dispatch("receipt/createReceipt", {
          orderId: this.tempPayment.objectId
        });
        
        if (receipt) {
          paymentData.id = receipt.objectId;
          await this.$store.dispatch("receipt/payReceipt", paymentData);
        }
      }
    },
    async processReceiptPayment(paymentData) {
      if (this.tempPaymentReceipt) {
        paymentData.id = this.tempPaymentReceipt.objectId;
        await this.$store.dispatch("receipt/payReceipt", paymentData);
      }
    },
    formatPaymentData(data){
      return {
        date: data.date,
        paymentMethod: data.paymentMethod,
        paymentBankAccount: data.paymentBankAccount,
        paymentNote: data.paymentNote,
        paymentProofFiles: data.paymentProofFiles,
        paymentCurrencyCode: data.paymentCurrencyCode,
        totalPaymentValue: data.totalPaymentValue,
        exchangeRate: data.exchangeRate,
        voucherNumber: data.voucherNumber
      }
    }
  },
  watch: {
    value(){
      if(this.value){
       this.initData();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .box-information{
    border-radius: 5px;
    border: 1px solid #E4E6E8;
    .header{
      color: #424242;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background-color:#EEEEEE;
      padding: 15px;
      font-size: 16px;
    }
    .body{
      padding: 15px;
      color:#424242;
      font-size: 16px;
    }
  }

.summary-topic {
  margin: 5px 0;
  color: #424242;
  font-weight: 400;
  font-size: 16px;
  &.bold {
    font-weight: 600;
  }
}

.summary-number {
  margin: 5px 0;
  font-weight: 400;
  color: #424242;
  font-size: 16px;
  &.bold {
    font-weight: 700;
  }
}

.box-meter-info{
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin: 20px 0;
  .meter-current-info{
    display: flex;
    align-items:center;
    justify-content: space-evenly;
    background-color: #FAFAFA;
    position: relative;
    border-radius: 10px;
    .status-meter{
      position: absolute;
      top: 5px;
      right: 5px;
    }
    .icon-meter{
      max-width: 100px;
      width: 100%;
    }
  }

  .meter-history{
    border-radius: 10px;
    border: 1px solid rgba(89, 89, 89, 0.12);
    padding: 0 15px 15px 15px;
    .header{
      color: #595959;
      font-size: 20px;
      border-bottom: 1px solid rgba(89, 89, 89, 0.12);
      font-weight: bold;
      padding: 15px 0;
    }
    .body{
      .history-container{
        overflow-y: auto;
        max-height: 155px;
        .history-item{
          display: flex;
          justify-content: space-between;
          padding: 10px 0;
          &:not(:last-child){
            border-bottom: 1px solid rgba(89, 89, 89, 0.12);
          }
        }
      }
    }
  }
}

.box-sell-meter{
  background: var(--v-bgPrimary-base);
  border: 1px solid  var(--v-primary-base);
  border-radius: 5px;
  padding: 20px;
  box-shadow: 0px 12px 44px 0px rgba(0, 0, 0, 0.12);
  .sell-section{
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 20px;
    align-items: end;
  }
}

.btn-history{
  background-color: var(--v-light-base);
  color: #000000;
  border-radius: 10px;
  &.active{
    background-color: #04926B !important;
    color: var(--v-light-base);
  }
}

.box-list-product{
  margin: 25px 0;
}

.divider-dashed{
  display: block;
  height: 1;
  border: 1px dashed var(--v-bgDarkgray-base);
}

.btn-link{
  padding: 0 !important;
  font-size: 16px;
  margin: 0 !important;
}

.table-item.header {
  background-color: rgb(0, 166, 80);

  th {
    color: white !important;
    font-size: 14px;
  }
}

.box-bordered{
  border: 1px solid #E0F2F1;
  padding: 15px;
  border-radius: 10px;
  .header{
    border-bottom: 1px solid #E0F2F1;
  }
  .actions{
    padding-top: 10px;
    border-top: 1px solid #E0F2F1;
  }
}

.shadow-card-bill{
  box-shadow: 0px 12px 44px 0px #0000001F !important;
  border-radius: 10px;
}

.text-color-black{
  color: #424242;
}

.custom-list{
  .v-list-item{
    min-height: 30px !important;
    min-width: 250px;
    margin-top: 5px;
  }
}

.box-payment-container{
  border : 1px solid #E0F2F1;
  border-radius: 5px;
  padding: 20px;
}
</style>

<style lang="scss">
.alert-unit {
  .v-alert__border{
    opacity: 1;
    border-width: 4px;
  }
}

.alert-on-off{
  .v-alert__icon {
    margin-top: 3px;
  }
}

</style>